import { memo, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { InferType } from 'yup';
import { useGlobalSettings } from 'providers';
import { useLocationParams } from 'hooks';
import { tikTokSchema } from '../validation/tiktok.schema';
import TikTokForm from './TikTokForm';
import {
  buildPacksFromCreativesByPack,
  getDefaultTikTokPack,
} from '../utils/buildPacksFromCreativesByPack';
import { idsFromSelection } from '../utils/idSelection';

export type TikTokFormValues = InferType<typeof tikTokSchema>;

const TikTokFormProvider = memo(() => {
  const { selectedApp } = useGlobalSettings();
  const {
    app: appParam,
    adAccount: adAccountParam,
    creativesByPack: creativesByPackParam,
    creativesFormat: creativesFormatParam,
    selectedCampaigns: selectedCampaignsParam,
    selectedAdSets: selectedAdSetsParam,
    filterCampaignsByName: filterCampaignsByNameParam,
    filterAdSetsByName: filterAdSetsByNameParam,
  } = useLocationParams();

  const packsCompatible = creativesFormatParam !== 'flat';

  const defaultValues = useMemo<TikTokFormValues>(
    () => {
      const packsFromUrl = packsCompatible
        ? buildPacksFromCreativesByPack(creativesByPackParam)
        : [];
      const restoredCreatives = packsFromUrl[0]?.creatives ?? [];

      return {
        currentStep: 0,
        app: appParam ?? selectedApp ?? '',
        adType: 'SINGLE_VIDEO',
        accountId: adAccountParam ?? '',
        selectedAdGroups: idsFromSelection(selectedAdSetsParam),
        selectedCampaigns: idsFromSelection(selectedCampaignsParam),
        selectedCampaignsOnly: false,
        selectedCreatives: restoredCreatives,
        packsWithCreatives: packsFromUrl.length ? packsFromUrl : [getDefaultTikTokPack()],
        activePacksWithCreatives: 0,
        activePackTag: '',
        filterCreativeByName: '',
        filterCampaignsByName: filterCampaignsByNameParam,
        filterAdSetsByName: filterAdSetsByNameParam,
        $adSetsData: [],
      };
    },
    [
      adAccountParam,
      appParam,
      creativesByPackParam,
      filterAdSetsByNameParam,
      filterCampaignsByNameParam,
      packsCompatible,
      selectedAdSetsParam,
      selectedApp,
      selectedCampaignsParam,
    ],
  );

  const formMethods = useForm<TikTokFormValues>({
    resolver: yupResolver(tikTokSchema),
    defaultValues,
  });

  return (
    <FormProvider {...formMethods}>
      <TikTokForm />
    </FormProvider>
  );
});

TikTokFormProvider.displayName = 'TikTokFormWrapper';

export default TikTokFormProvider;
